import React, { useState } from 'react'
import SEO from '../components/SEO'

export default function KontaktPage() {
  const [formData, setFormData] = useState({
    navn: '',
    telefon: '',
    email: '',
    beskrivelse: '',
    website_url: '', // Honeypot
  })

  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrorMessage('')

    if (!formData.navn.trim() || !formData.telefon.trim() || !formData.email.trim()) {
      setErrorMessage('Udfyld venligst navn, telefonnummer og e-mailadresse.')
      return
    }

    setSubmitting(true)

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          form_type: 'Kontaktformular',
          ...formData,
        }),
      })

      if (response.ok) {
        setSubmitted(true)
      } else {
        setErrorMessage('Der opstod en fejl ved afsendelsen. Prøv venligst igen eller ring på 40 20 70 34.')
      }
    } catch (err) {
      setErrorMessage('Der opstod en netværksfejl. Prøv venligst igen eller ring på tlf. 40 20 70 34.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="bg-slate-50 min-h-screen py-12">
      <SEO
        title="Kontakt - Nordfyn Marine"
        description="Kontakt Nordfyn Marine for rådgivning om køb og salg af båd. Nordfyn Marine tlf. 40 20 70 34."
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
        {/* Header Hero */}
        <div className="bg-[#0B1B2B] text-white p-8 sm:p-12 rounded-2xl shadow-lg relative overflow-hidden">
          <div className="relative z-10 space-y-3">
            <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#2BABE3]">
              Vi svarer hele døgnet
            </span>
            <h1 className="text-3xl sm:text-4xl font-serif font-bold text-white">
              Kontakt Nordfyn Marine
            </h1>
            <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
              Vi står altid klar til en uforpligtende snak om køb, salg eller fremvisning af båd. Ring til os, send en e-mail eller brug formularen.
            </p>
          </div>
        </div>

        {/* Contact Info & Form Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Info Sidecard */}
          <div className="bg-[#071320] text-white p-6 sm:p-8 rounded-2xl border border-slate-800 shadow-xl space-y-6">
            <div>
              <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#2BABE3]">
                Virksomhedsoplysninger
              </span>
              <h2 className="font-serif font-bold text-2xl text-white mt-1">
                Nordfyn Marine
              </h2>
              <p className="text-xs text-slate-300 mt-1">
                Nordfyn Marine
              </p>
            </div>

            <div className="space-y-4 pt-2 text-xs text-slate-200">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#1E7BC1]/20 text-[#2BABE3] flex items-center justify-center shrink-0">
                  <i className="fa-solid fa-user" />
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-semibold">
                    Kontaktperson
                  </span>
                  <span className="font-semibold text-white">Nordfyn Marine</span>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#1E7BC1]/20 text-[#2BABE3] flex items-center justify-center shrink-0">
                  <i className="fa-solid fa-phone" />
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-semibold">
                    Telefon (Svarer hele døgnet)
                  </span>
                  <a
                    href="tel:40207034"
                    className="font-bold text-base text-[#1E7BC1] hover:underline whitespace-nowrap"
                  >
                    40 20 70 34
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#1E7BC1]/20 text-[#2BABE3] flex items-center justify-center shrink-0">
                  <i className="fa-solid fa-envelope" />
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-semibold">
                    E-mail
                  </span>
                  <a
                    href="mailto:info@nordfyn-marine.dk"
                    className="font-semibold text-white hover:text-[#2BABE3] transition-colors whitespace-nowrap"
                  >
                    info@nordfyn-marine.dk
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#1E7BC1]/20 text-[#2BABE3] flex items-center justify-center shrink-0">
                  <i className="fa-solid fa-location-dot" />
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-semibold">
                    Adresse
                  </span>
                  <span className="font-semibold text-white">Stationsvej 24, 5450 Otterup</span>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#1E7BC1]/20 text-[#2BABE3] flex items-center justify-center shrink-0">
                  <i className="fa-solid fa-id-card" />
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-semibold">
                    CVR
                  </span>
                  <span className="font-semibold text-white">14959335</span>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-[#0B1B2B] border border-slate-800 text-xs text-slate-300 space-y-1">
              <span className="font-bold text-[#2BABE3] block">Besigtigelse af både:</span>
              <p className="text-[11px] leading-relaxed">
                Aftal gerne besigtigelse på forhånd, så vi er klar til at tage imod dig og vise båden frem.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            {submitted ? (
              <div className="bg-white p-8 sm:p-12 rounded-2xl border border-emerald-200 text-center space-y-4 shadow-sm h-full flex flex-col justify-center items-center">
                <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-2xl">
                  <i className="fa-solid fa-check" />
                </div>
                <h2 className="font-serif font-bold text-2xl text-slate-900">
                  Mange tak for din besked!
                </h2>
                <p className="text-xs text-slate-600 max-w-md leading-relaxed">
                  Vi har modtaget din besked og vender tilbage til dig hurtigst muligt.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-white p-6 sm:p-10 rounded-2xl border border-slate-200 shadow-sm space-y-6"
              >
                <h2 className="font-serif font-bold text-2xl text-slate-900 border-b border-slate-100 pb-3">
                  Send en besked
                </h2>

                {/* Honeypot Field */}
                <div className="sr-only absolute" aria-hidden="true">
                  <label htmlFor="website_url">Website URL</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    tabIndex="-1"
                    autoComplete="off"
                    value={formData.website_url}
                    onChange={handleChange}
                  />
                </div>

                {errorMessage && (
                  <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs font-medium flex items-center gap-3">
                    <i className="fa-solid fa-circle-exclamation text-base shrink-0" />
                    <span>{errorMessage}</span>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                      Navn <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="navn"
                      required
                      value={formData.navn}
                      onChange={handleChange}
                      placeholder="Dit fulde navn"
                      className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#C5A059]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                      Telefonnummer <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="telefon"
                      required
                      value={formData.telefon}
                      onChange={handleChange}
                      placeholder="Tlf. nummer"
                      className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#C5A059]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    E-mailadresse <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Din e-mailadresse"
                    className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#C5A059]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Besked / Beskrivelse
                  </label>
                  <textarea
                    name="beskrivelse"
                    rows="5"
                    value={formData.beskrivelse}
                    onChange={handleChange}
                    placeholder="Skriv din besked her..."
                    className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#C5A059]"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto bg-[#1E7BC1] hover:bg-[#2BABE3] text-white px-8 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] transition-all shadow-md flex items-center justify-center gap-2 rounded-xl"
                >
                  {submitting ? (
                    <>
                      <i className="fa-solid fa-spinner animate-spin" />
                      <span>Sender besked...</span>
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-paper-plane" />
                      <span>Send besked</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
